import { useAuth } from '@/hooks/use-auth/use-auth';
import { useLogout } from '@/hooks/use-logout/use-logout';
import { useUser } from '@/hooks/use-user/use-user';
import { CallTakerLayout } from '@/layouts/call-taker-layout/call-taker-layout';
import DefaultLayout from '@/layouts/default-layout/default.layout';
import { httpClient, renewSession } from '@/services/request/request.service';
import { setAuthFailureHandler } from '@/services/websocket/ws-client';
import surface from '@/styles/page-surface.module.scss';
import { Spin } from 'antd';
import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

type PrivateRouteProps = {
  children: React.ReactNode;
};

// These answer 404 for ordinary states: a campaign or lead the user cannot see, a company
// that is not theirs, or `salespeople/me/availability` for someone who is not a salesperson.
// Their screens say so in place; a jump to /404 would throw away what the user was doing.
const NOT_FOUND_IN_PLACE = ['outbound_calls'];

const redirectsOnNotFound = (url: string | undefined) =>
  !NOT_FOUND_IN_PLACE.includes((url ?? '').replace(/^\/+/, '').split(/[/?]/)[0]);

export const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const { logout } = useLogout();
  const { isAuthorized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, isLoading } = useUser();

  const isCallTaker = userData?.roles.some((role) => role.name === 'call_taker');

  useEffect(() => {
    const interceptor = httpClient.interceptors.response.use(
      (res: AxiosResponse) => res,
      (err: AxiosError) => {
        if (
          err?.response?.status === HttpStatusCode.NotFound &&
          redirectsOnNotFound(err.config?.url)
        ) {
          navigate('/404', { replace: true });
        }
        // Reject, never return: returning turns every error into a resolved value for every
        // caller. 401 is not handled here any more, request.service owns it.
        return Promise.reject(err);
      }
    );

    return () => httpClient.interceptors.response.eject(interceptor);
  }, [navigate]);

  // The WebSocket half of the same 401. The interceptor above cannot cover it: the
  // realtime queries are pinned at staleTime: Infinity, so an idle tab issues no request.
  // 4401 is not terminal any more. Renew first, and log out only on a rejected credential,
  // or an idle tab would be signed out at the token's exp while its session is still
  // renewable. A successful renewal re-dials the sockets itself.
  //
  // 'blocked' leaves the sockets down without ending the session, matching the interceptor:
  // a 429 off a shared address or a blip while every channel closes 4401 says nothing about
  // the credential. The next successful HTTP refresh re-dials them.
  useEffect(() => {
    setAuthFailureHandler(() => {
      void renewSession().then((outcome) => {
        if (outcome !== 'rejected') return;
        logout();
        navigate('/sign-in', { state: { from: location.pathname } });
      });
    });

    return () => setAuthFailureHandler(null);
  }, [logout, navigate, location.pathname]);

  if (!isAuthorized) {
    return <Navigate to="/sign-in" state={{ from: location?.pathname ?? '/' }} replace />;
  }

  if (isLoading || !userData) {
    return (
      // Renders before DefaultLayout mounts, so it paints the page colour itself rather
      // than flashing an unpainted viewport on every hard refresh.
      <Spin
        className={surface.pageSurface}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      />
    );
  }

  if (!userData.is_admin && userData.self_registered && !userData.subscribed) {
    return <Navigate to="/onboarding/plan" replace />;
  }

  if (isCallTaker) {
    return <CallTakerLayout>{children}</CallTakerLayout>;
  }

  return <DefaultLayout>{children}</DefaultLayout>;
};
